import { InlineError, InlineNotice, Text } from '@box/blueprint-web';
import classNames from 'classnames';
import * as React from 'react';
import { FormattedMessage, injectIntl, useIntl } from 'react-intl';
import { MODE } from '../../../constants';
import { useModeContext } from '../../contexts/mode-context';
import useId from '../../utils/useId';
import DropZone from '../drop-zone';
import css from './blueprint-upload-field.module.scss';
import messages from './messages';
import { type UploadFieldProps } from './types';
import { useSplitTreatment } from '../../split-treatment';
import { FEATURE_FLAGS } from '../../../constants/feature-flags';
import { FolderSelector } from '../folder-selector/folder-selector';
import { isFieldError, isMessageDescriptor } from './types-utils';

const BlueprintUploadField = (props: UploadFieldProps) => {
  const intl = useIntl();
  const {
    className = '',
    label,
    description,
    error,
    isRequired,
    isDisabled,
    onAddItems,
    onRemoveItem,
    items,
    elementId,
    shouldShowFolderSelector,
  } = props;
  const hasError = !!error;
  const classes = classNames(className, css.uploadFieldContainer, {
    // @ts-expect-error [MIGRATION-SUPPRESSION]: TS2339
    [css.disabled]: isDisabled,
  });

  const { mode } = useModeContext();
  const isForm = MODE.FORM === mode;

  const isInlineFolderSelectorEnabled = useSplitTreatment(
    FEATURE_FLAGS.FORMS_ENABLE_INLINE_FOLDER_SELECTOR,
  );

  const labelID = useId('label');
  const errorMessageID = useId('inline-error-');
  const descriptionID = useId('description');

  const ariaDescribedBy = [
    ...(description ? [descriptionID] : []),
    errorMessageID,
  ].join(' ');
  const ariaAttrs = {
    'aria-invalid': hasError,
    'aria-labelledby': labelID,
    'aria-required': isRequired,
    'aria-describedby': ariaDescribedBy,
  };

  return (
    <div className={classes} data-testid="blueprint-upload-field">
      <Text
        id={labelID}
        as="label"
        // @ts-expect-error [MIGRATION-SUPPRESSION]: TS2820
        color="text-on-light-default"
        variant="subtitle"
      >
        {label}
      </Text>

      {!!description && (
        <Text
          as="span"
          className={css.description}
          // @ts-expect-error [MIGRATION-SUPPRESSION]: TS2322
          color="textOnLight"
          id={descriptionID}
          variant="bodyDefault"
        >
          {description}
        </Text>
      )}

      {hasError && !isForm && (
        <InlineNotice
          className={css.errorInlineNotice}
          data-testid="blueprint-upload-inline-error"
          id={errorMessageID}
          variant="error"
          variantIconAriaLabel={intl.formatMessage(messages.errorIconAriaLabel)}
        >
          {isFieldError(error) && <FormattedMessage {...error.message} />}
          {isMessageDescriptor(error) && <FormattedMessage {...error} />}
        </InlineNotice>
      )}

      {isForm && shouldShowFolderSelector && isInlineFolderSelectorEnabled ? (
        <div className={css.folderSelector}>
          <FolderSelector
            elementId={elementId}
            error={error}
            shouldShowPanelError={false}
          />
        </div>
      ) : (
        <DropZone
          className={classNames('dropzone', css.dropZone)}
          isDisabled={isDisabled}
          items={items}
          onAddItems={onAddItems}
          onRemoveItem={onRemoveItem}
          {...ariaAttrs}
        />
      )}

      {isForm && error && (
        <InlineError
          className={css.bottomError}
          data-testid="blueprint-upload-error-below-dropzone"
          id={errorMessageID}
        >
          {isFieldError(error) && <FormattedMessage {...error.message} />}
          {isMessageDescriptor(error) && <FormattedMessage {...error} />}
        </InlineError>
      )}
    </div>
  );
};

// @ts-expect-error [MIGRATION-SUPPRESSION]: TS2304
type WrappedProps = Omit<Props, 'intl'>;
// @ts-expect-error [MIGRATION-SUPPRESSION]: TS2769
const Wrapped = injectIntl(BlueprintUploadField);
export default Wrapped as React.ComponentType<WrappedProps>;
